// Application shell for every authenticated route: the gov.md bar, a left
// sidebar, the workspace, and the MUD footer that carries the support contacts
// ACCS-10 requires on every page.
//
// The strip is here as well as on `PublicShell` by the owner's ruling of
// 2026-09-09, which supersedes the ruling of 2026-09-06 that had taken it back
// out. The wireframe is no longer the deciding reading: MUD carries the strip
// as `.top-section` (`mud/components/header.md:74`, node `1144:15205`, 32 tall,
// with Desktop 1200 / Desktop 1280 / Mobile breakpoints) above `header-general-*`,
// and that header has both Anonymous and Authenticated user states. So the strip
// is not anonymous-page chrome in MUD — it is the state's band above whatever
// header follows it, signed in or not. See `docs/ui-shape-govmd-bar.md §The strip`.
// per docs/ui-shape.md §Sidebar application shell (owner decision, 2026-08-26)
// per docs/ui-shape-govmd-bar.md §Where the sidebar goes
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Link, Outlet, useLocation, useParams } from 'react-router-dom';
import { useTranslate } from '@tolgee/react';
import { MudButton, MudIcon } from '@egov-moldova/mud-react';
import { Sidebar } from './Sidebar';
import { Footer } from './Footer';
import { GovBar } from './GovBar';
import { SearchOverlay } from '../Search/SearchOverlay';
import { getRegistrarEpoch, subscribeActiveRegistrar } from '../../app/active-registrar';
import { ActiveRegistrarGate } from '../RegistrarSelector/ActiveRegistrarGate';
import { useGlobalSearchHotkey } from '../Search/useGlobalSearchHotkey';

// Below this width a 320px rail would leave the admin tables under 500px of
// workspace, so the sidebar goes off-canvas instead (ACCS-02 floor is 480px).
const DESKTOP_QUERY = '(min-width: 1024px)';
const COLLAPSED_KEY = 'simsm.nav.collapsed';

function matches(query: string): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(query).matches
    : true;
}

function useIsDesktop(): boolean {
  const subscribe = useCallback((onChange: () => void) => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return () => undefined;
    }
    const mq = window.matchMedia(DESKTOP_QUERY);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return useSyncExternalStore(
    subscribe,
    () => matches(DESKTOP_QUERY),
    () => true,
  );
}

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
}

export function AppShell(): JSX.Element {
  const { t } = useTranslate();
  const { role } = useParams<{ role: string }>();
  const location = useLocation();
  const isDesktop = useIsDesktop();
  const [searchOpen, setSearchOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [navOpen, setNavOpen] = useState(false);
  const topbarRef = useRef<HTMLDivElement>(null);
  useGlobalSearchHotkey(() => setSearchOpen(true));
  const registrarEpoch = useSyncExternalStore(subscribeActiveRegistrar, getRegistrarEpoch, () => 0);

  // The panel must never cover the page it just navigated to, including on
  // browser back and programmatic redirects. A route change that closes the
  // panel with the focus inside it (a role switched from the account menu, 059
  // V-137) would leave the focus in a hidden panel, where it falls to <body>; it
  // goes back to the button that opens the panel, as a close by hand does.
  const navOpenRef = useRef(navOpen);
  navOpenRef.current = navOpen;
  useEffect(() => {
    const panel = document.getElementById('primary-navigation');
    if (navOpenRef.current && panel?.contains(document.activeElement)) {
      topbarRef.current?.querySelector<HTMLElement>('.simsm-shell__nav-toggle')?.focus();
    }
    setNavOpen(false);
  }, [location.pathname]);

  // Closing the panel by hand (Escape, the scrim, the panel's own close button or
  // a row in it) gives the focus back to the button that opened it, as a dialog
  // does; left inside a panel that is now hidden, it fell to <body>.
  const closeNav = useCallback((): void => {
    setNavOpen(false);
    topbarRef.current?.querySelector<HTMLElement>('.simsm-shell__nav-toggle')?.focus();
  }, []);

  useEffect(() => {
    if (!navOpen) return undefined;
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') closeNav();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [navOpen, closeNav]);

  function toggleCollapsed(): void {
    setCollapsed((previous) => {
      const next = !previous;
      try {
        window.localStorage.setItem(COLLAPSED_KEY, String(next));
      } catch {
        /* a private-mode browser still gets a working shell */
      }
      return next;
    });
  }

  const offCanvas = !isDesktop;
  const railCollapsed = isDesktop && collapsed;
  const shellClass = [
    'simsm-shell',
    railCollapsed ? 'simsm-shell--collapsed' : '',
    offCanvas ? 'simsm-shell--off-canvas' : '',
    offCanvas && navOpen ? 'simsm-shell--nav-open' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={shellClass}>
      {/* Above the rail and the workspace both, because it is the state's strip
          and not the application's chrome — MUD's `.top-section` sits above the
          header, not inside it. Owner ruling 2026-09-09. */}
      <GovBar />

      <div className="simsm-shell__body">
        {offCanvas ? (
          <div className="simsm-shell__topbar" ref={topbarRef}>
            <MudButton
              className="simsm-shell__nav-toggle"
              shape="circular"
              variant="strict"
              appearance="text"
              size="sm"
              iconOnly
              label={t('nav.drawer.open')}
              aria-expanded={navOpen}
              aria-controls="primary-navigation"
              onClick={() => setNavOpen(true)}
            >
              <MudIcon slot="icon" name="menu" size={20} />
            </MudButton>
            <Link to={`/${role ?? 'operator'}/home`} className="simsm-shell__topbar-brand">
              <img
                src="/brand/amdm-mark.png"
                alt={t('home.amdm.logo_alt')}
                width={28}
                height={28}
              />
              <span>{t('home.product.mark')}</span>
            </Link>
          </div>
        ) : null}

        <Sidebar
          collapsed={railCollapsed}
          onToggleCollapsed={toggleCollapsed}
          offCanvas={offCanvas}
          open={navOpen}
          onClose={closeNav}
        />

        {offCanvas && navOpen ? (
          <div className="simsm-shell__scrim" role="presentation" onClick={closeNav} />
        ) : null}

        <div className="simsm-shell__workspace">
          <main id="main-content" className="simsm-shell__main">
            <div className="simsm-shell__container">
              {/* The observer scope banner used to hang here, above every page, which put it above
                  every page title too. It now belongs to the observer screens themselves, directly
                  under their page head — see ObserverScopeBanner. */}
              {/* Keyed by the Registrar switch counter: a page keeps local state
                  (a location picked on a new report, a filter, an open dialog)
                  that the query reset does not reach, and none of it may carry
                  the Registrar being left. Gated by the choice itself: with
                  several Registrars and none chosen, no page renders (T168). */}
              <ActiveRegistrarGate>
                <Outlet key={registrarEpoch} />
              </ActiveRegistrarGate>
            </div>
          </main>
          <Footer />
        </div>
      </div>

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
}
